import type { JSX } from 'react';
import {
  Autocomplete,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { amber, green, grey, red } from '@mui/material/colors';
import SearchIcon from '@mui/icons-material/Search';
import ArticleIcon from '@mui/icons-material/Article';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import InfoIcon from '@mui/icons-material/Info';
import AddIcon from '@mui/icons-material/Add';
import CompareIcon from '@mui/icons-material/Compare';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  GridCellParams,
  GridColDef,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridValidRowModel,
  gridStringOrNumberComparator,
} from '@mui/x-data-grid';
import { t } from 'i18next';
import {
  ECellType,
  ECommonDialogType,
  EActionType,
  EDataTableType,
  EModuleType,
  EStatus,
  EStatusColor,
  EToolbarButton,
  FillFieldsWithValueAsNo,
  RecordLength,
  foreignKeysForTable,
} from '../../../data/Constants';
import { countryCodes } from '../../../localization';
import { formatDateString, getMaxDate } from '../../../services/DataHelperFunctions';
import { AppDispatch, AppStore } from '../../../store';
import { LinearProgressBar } from '../../Layouts/LinearProgressBar';
import { StructuredLogViewer } from '../../Helper/StructuredLogViewer';
import type {
  IButtonSectionHelperProps,
  IDataTableRow,
  IImageData,
  IMaintenanceTableProps,
  IOpenDialogFn,
  IPaginationRequest,
  IPropertyDefault,
  ITableHeaders,
  ITableSearchBarProps,
  ITableRecordPickerProps,
  ITablePaginationRowProps,
  IRenderCellOptions,
  IRenderEditCellCallbacks,
  TRenderCellFn,
} from '../../../../types';

// #region Log helpers

/**
 * Format the log details sent by API to show in readable format
 * @param {string} logMessage is log details of notification/queue
 * @param {function} t is the translation function
 * @returns {JSX.Element} the div element
 */
export const formatLogContent = (logMessage: string, t: any): JSX.Element => {
  let logText = null;

  if (logMessage === null) {
    return <div className="pt-1">{t('messages.noDataFound')}</div>;
  } else {
    // Removes all opening and closing { } brackets in message string
    // Split the string into individual lines using the '\n' delimiter
    const logLines = logMessage.replaceAll(/[{}]/g, '').split('\n');

    // Create an array of JSX elements, each representing a line
    logText = logLines?.map((line, index) => <div key={index + 1}>{line}</div>);

    return <div className="pt-1">{logText}</div>;
  }
};

/**
 * Format a JSON-structured log with a tabbed view — one tab per activity key
 * plus an "All" tab showing everything. Falls back to plain rendering for non-JSON.
 * @param {string} logMessage is log details from the ProductRelease API
 * @param {function} t is the translation function
 * @returns {JSX.Element} the div element
 */
export const formatStructuredLogContent = (logMessage: string, t: any): JSX.Element => {
  if (logMessage === null) {
    return <div className="pt-1">{t('messages.noDataFound')}</div>;
  }

  try {
    // Escape raw control characters that make JSON.parse fail when they appear
    // literally inside string values (e.g. embedded newlines from the API).
    const sanitized = logMessage
      .split('\n')
      .join(String.raw`\n`)
      .split('\r')
      .join(String.raw`\r`)
      .split('\t')
      .join(String.raw`\t`);
    const parsed = JSON.parse(sanitized);
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return <StructuredLogViewer entries={Object.entries(parsed)} />;
    }
  } catch (e) {
    return (
      <div className="pt-1" style={{ color: 'red' }}>
        {t('messages.errorParsingLog')}: {(e as Error).message}
      </div>
    );
  }

  const logLines = logMessage.replaceAll(/[{}]/g, '').split('\n');
  return (
    <div className="pt-1">
      {logLines.map((line, index) => (
        <div key={index + 1}>{line}</div>
      ))}
    </div>
  );
};

// #endregion

// #region TableSearchBar

/** Autocomplete search bar with a search icon adornment, shared across data tables. */
export const TableSearchBar = ({
  value,
  placeholder,
  onChange,
}: ITableSearchBarProps): JSX.Element => (
  <Autocomplete
    id="product-search-box"
    clearOnBlur
    freeSolo
    value={value}
    options={[]}
    className="search-bar"
    renderInput={(params) => (
      <Tooltip title={placeholder} arrow>
        <TextField
          {...params}
          className="search-inner-text"
          placeholder={placeholder}
          slotProps={{
            ...params.slotProps,
            input: {
              ...params.slotProps.input,
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />
      </Tooltip>
    )}
    onChange={(_, newVal) => onChange(newVal?.trim() ?? '')}
  />
);

// #endregion

// #region TableRecordPicker

/** Records-per-page dropdown, shared across data tables. */
export const TableRecordPicker = ({ value, onChange }: ITableRecordPickerProps): JSX.Element => (
  <div className="d-flex align-center recordPicker">
    {t('labels.recordsPerPage') + ':'}&emsp;
    <FormControl size="small" style={{ width: '5.5rem' }}>
      <Select value={value} onChange={(e) => onChange(Number(e.target.value))}>
        {RecordLength.map((r) => (
          <MenuItem key={r} value={r}>
            {r}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  </div>
);

// #endregion

// #region TablePaginationRow

/** Centred MUI Pagination row. Renders nothing when `show` is false. */
export const TablePaginationRow = ({
  count,
  page,
  show,
  onChange,
}: ITablePaginationRowProps): JSX.Element | null => {
  if (!show) return null;
  return (
    <Stack spacing={2} style={{ alignItems: 'center' }}>
      <Pagination color="primary" count={count} page={page} onChange={onChange} />
    </Stack>
  );
};

// #endregion

// #region renderNoRecordsMessage

/**
 * Empty-state overlay for data tables.
 *
 * Shows a red message when a search returns no results, and a neutral message
 * when the table has no data at all.
 *
 * @param searchValue - The active search string; empty string means no search is active.
 */
export const renderNoRecordsMessage = (searchValue: string): JSX.Element => (
  <Stack direction="row" sx={{ height: '100%', justifyContent: 'center', alignItems: 'center' }}>
    <span style={{ color: searchValue ? 'red' : 'black' }}>
      {searchValue ? t('messages.noSearchRecords', { searchValue }) : t('messages.noDataFound')}
    </span>
  </Stack>
);

// #endregion

// #region Column factory

/**
 * Computes the filter/sort value for a column cell.
 * Handles ObjectChips, ArrayChips, Object, Images, Date, MaxDate, and boolean types.
 * Falls back to the raw field value for all other types.
 */
export const valueGetterData = (header: ITableHeaders, params: GridValidRowModel): string => {
  if (header.type === ECellType.ObjectChips || header.type === ECellType.ArrayChips) {
    return params[header.field]
      ? (params[header.field] as { code?: string; name?: string }[])
          .map((role) => role.code ?? role.name ?? '')
          .join(', ')
      : '';
  }
  if (header.type === ECellType.Object) {
    return (params[header.field] as IPropertyDefault)?.name ?? '';
  }
  if (header.type === ECellType.Images) {
    return (params[header.field] as { type: string }[]).map((role) => role.type).join(', ');
  }
  if (header.type === ECellType.Date) {
    return formatDateString(params[header.field] as string);
  }
  if (header.type === ECellType.MaxDate) {
    const datesArr = (params[header.key as string] as { modifiedOn: string }[]).map(
      (data) => data.modifiedOn
    );
    return getMaxDate(datesArr);
  }
  if (typeof params[header.field] === 'boolean') {
    return params[header.field] ? t('labels.yes') : t('labels.no');
  }
  return params[header.field] as string;
};

/**
 * Builds the common `GridColDef` properties shared by all data tables.
 *
 * Handles: field name/label, sortable, width/flex, filterable, `cellWithValueAsNo` class,
 * searchable-column header indicator, valueGetter (all ECellType variants), and sortComparator.
 *
 * Each table passes its own `renderCellFn` and may spread additional props on top.
 *
 * @param header - Column header definition from TableHeaders
 * @param renderCellFn - The renderCell implementation for this table
 */
export const createBaseColumn = (
  header: ITableHeaders,
  renderCellFn: TRenderCellFn
): GridColDef => ({
  field: header.field,
  headerName: header.hasNoTranslation ? header.field : t('labels.' + header.field),
  sortable: header.sort,
  width: header.width,
  flex: header.flex,
  filterable: header.filter !== false,
  cellClassName: (params: GridCellParams) =>
    FillFieldsWithValueAsNo.includes(header.field) && !params.row[header.field]
      ? 'cellWithValueAsNo'
      : '',
  renderHeader: () => (
    <span className="searchIconForColumn">
      {header.searchable ? <SearchIcon color="primary" /> : null}
      {header.hasNoTranslation ? header.field : t('labels.' + header.field)}
    </span>
  ),
  renderCell: renderCellFn,
  valueGetter: (_: unknown, params: GridValidRowModel) => valueGetterData(header, params),
  sortComparator: gridStringOrNumberComparator,
});

/**
 * Static DataGrid props that are identical across all maintenance tables.
 * Spread these onto every `<DataGrid>` and add table-specific props alongside.
 */
export const sharedDataGridProps = {
  disableRowSelectionOnClick: true as const,
  disableColumnMenu: true as const,
  hideFooter: true as const,
  rowHeight: 35,
  columnHeaderHeight: 35,
  style: { height: '75%' as const },
  showToolbar: true as const,
  slotProps: {
    columnsManagement: { disableShowHideToggle: true },
  },
};

// #endregion

// #region renderCell

/** Renders a row of Chip components for ECellType.Images, each opening an image dialog on click. */
const getImages = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn
): JSX.Element => (
  <Stack direction="row" spacing={1}>
    {params.row[col.field].map((r: IImageData) => (
      <Chip
        size="small"
        key={r.type}
        label={r.type}
        onClick={() => openDialog(r as unknown as IDataTableRow, ECommonDialogType.Image)}
        color="primary"
        variant={r.isDefault ? 'filled' : 'outlined'}
      />
    ))}
  </Stack>
);

/** Info icon button that opens the detail dialog for a row. */
const renderIconButton = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn
): JSX.Element => (
  <IconButton
    data-testid="btn-info-dialog"
    onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}
  >
    <InfoIcon color="primary" />
  </IconButton>
);

/** Maps an EStatus value to its corresponding icon. Returns null for unrecognised statuses. */
const getStatusIcon = (
  status: string,
  fontSize: 'small' | 'medium' | 'inherit' = 'medium'
): JSX.Element | null => {
  switch (status) {
    case EStatus.Success:
      return <CheckCircleIcon color="success" fontSize={fontSize} />;
    case EStatus.Failed:
      return <CancelIcon color="error" fontSize={fontSize} />;
    case EStatus.Processing:
    case EStatus.Initial:
      return <CheckCircleIcon sx={{ color: amber[500] }} fontSize={fontSize} />;
    case EStatus.New:
      return <CheckCircleIcon color="primary" fontSize={fontSize} />;
    default:
      return null;
  }
};

/** Wraps getStatusIcon in an IconButton for ECellType.Status cells. */
const showStatusOfNotification = (params: GridRenderCellParams): JSX.Element => (
  <IconButton>{getStatusIcon(params.row?.status) || ''}</IconButton>
);

/** Info icon button whose colour reflects the sync status: amber=warning, green=success, grey=no data, red=error. */
const InfoIconWithColor = (
  params: GridRenderCellParams,
  openDialog: IOpenDialogFn,
  col: ITableHeaders,
  status: string
): JSX.Element => {
  let iconColor;
  if (status === EStatusColor.Warning) {
    iconColor = amber[500];
  } else if (status === EStatusColor.Success) {
    iconColor = green[500];
  } else if (status === EStatusColor.NoData) {
    iconColor = grey[500];
  } else {
    iconColor = red[700];
  }
  return (
    <IconButton onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}>
      <InfoIcon sx={{ color: iconColor }} />
    </IconButton>
  );
};

/** Article/log icon button that opens the log or error dialog for a row. */
const getArticleIcon = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog: IOpenDialogFn
): JSX.Element => (
  <div>
    <IconButton onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}>
      <ArticleIcon color="primary" />
    </IconButton>
  </div>
);

/**
 * Download icon button for ECellType.Download cells.
 * Disabled when the row status is not Success, or when a report row has no fileName.
 * Returns null when no download callback is provided.
 */
const getDownloadIcon = (
  params: GridRenderCellParams,
  moduleType: string,
  download?: (
    action: { productCode?: string; versionId?: string; id?: string },
    dispatch: AppDispatch
  ) => void
): JSX.Element | null => {
  if (!download) return null;
  const disableBtn =
    params.row?.status !== EStatus.Success ||
    (moduleType === EModuleType.Reports && !params.row?.fileName);
  return (
    <div>
      <IconButton
        disabled={disableBtn}
        onClick={() =>
          download(
            moduleType === EModuleType.Reports
              ? { id: params.row.id }
              : { productCode: params.row.productCode, versionId: params.row.version },
            AppStore.dispatch
          )
        }
      >
        <FileDownloadIcon color={disableBtn ? 'disabled' : 'primary'} />
      </IconButton>
    </div>
  );
};

/** Truncated text with a hover tooltip; appends an info icon when `col.hasIcon` is true. */
const renderTooltipWithIcon = (
  val: string,
  col: ITableHeaders,
  params: GridRenderCellParams,
  openDialog: IOpenDialogFn
): JSX.Element => (
  <>
    <Tooltip title={val} placement="bottom" arrow>
      <span className="overflow-hidden overflow-ellipsis">{val}</span>
    </Tooltip>
    {val && col.hasIcon && renderIconButton(params, col, openDialog)}
  </>
);

/** Renders a diff cell: "old / new" with the removed part struck and the added part highlighted. */
const renderUpdateCell = (value: string): JSX.Element => {
  const parts = value.includes('/') ? value.split('/') : null;
  return parts ? (
    <span className="overflow-hidden overflow-ellipsis updateCell">
      <span className="removed">{parts[0]}</span>/ <span className="added">{parts[1]}</span>
    </span>
  ) : (
    <span className="overflow-hidden overflow-ellipsis">{value}</span>
  );
};

/** Parses the syncStatus JSON and returns an EStatusColor: Success when all values are set, Warning otherwise. */
const renderSyncStatus = (syncStatus: string): string => {
  const syncData = JSON.parse(syncStatus) as Record<string, string>;
  if (!syncData) return EStatusColor.NoData;
  const allKeysHaveValues = Object.values(syncData).every((val) => val.trim() !== '');
  return allKeysHaveValues ? EStatusColor.Success : EStatusColor.Warning;
};

/** Extracts the display name from an IPropertyDefault object, or returns an empty string. */
const getDefaultValue = (value: IPropertyDefault): string => (value ? value.name : '');

/** @private Renders the Link cell, routing clicks to onCellClick or openDialog. */
const renderLinkCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  type: string,
  onCellClick?: (row: IDataTableRow, field: string) => void,
  openDialog?: IOpenDialogFn
): JSX.Element => {
  const fieldValue = params.row[col.field];
  const handleClick = (): void => {
    if (onCellClick) {
      onCellClick(params.row as IDataTableRow, col.field);
    } else {
      openDialog?.(params.row, type, EActionType.Edit);
    }
  };
  return (
    <button
      className="btn-plain link justify-left p-0"
      onClick={handleClick}
      aria-label={onCellClick ? String(fieldValue) : `Edit ${fieldValue}`}
    >
      {fieldValue}
    </button>
  );
};

/** @private Renders the Chips (retrigger) cell. */
const renderChipsCell = (
  params: GridRenderCellParams,
  type: string,
  openDialog?: IOpenDialogFn,
  props?: IMaintenanceTableProps
): JSX.Element => {
  const hasActivities =
    params.row.activities && Object.keys(JSON.parse(params.row.activities as string)).length > 0;
  let retriggerDisable: boolean;
  if (type === EDataTableType.ProductRelease) {
    retriggerDisable =
      !hasActivities || ![EStatus.Success, EStatus.Failed].includes(params.row.status as EStatus);
  } else {
    retriggerDisable = !params.row.isActive;
  }
  const handleChipClick = (): void => {
    if (type === EDataTableType.ProductRelease) {
      openDialog?.(params.row, ECommonDialogType.RetriggerProductRelease);
    } else if (props?.retrigger) {
      AppStore.dispatch(
        props.retrigger(
          (params.row[foreignKeysForTable[type]] as string) || (params.row.code as string)
        )
      );
    }
  };
  return (
    <Chip
      size="small"
      key={params.row.code}
      onClick={handleChipClick}
      label={t('labels.retrigger')}
      color="primary"
      variant="outlined"
      disabled={retriggerDisable}
    />
  );
};

/** @private Renders the default (plain text) cell, bolding rows that have a child option. */
const renderDefaultCell = (params: GridRenderCellParams, col: ITableHeaders): JSX.Element => {
  const bold = params.row.hasOption ? 'text-bold' : '';
  return (
    <span className={`overflow-hidden overflow-ellipsis ${bold}`}>{params.row[col.field]}</span>
  );
};

/** @private Renders an ObjectChips cell: tooltip+icon when a dialog is available, plain text otherwise. */
const renderObjectChipsCell = (
  fieldValue: unknown,
  col: ITableHeaders,
  params: GridRenderCellParams,
  openDialog?: IOpenDialogFn
): JSX.Element => {
  const val = (fieldValue as { code?: string; name?: string }[])
    .map((data) => data.code ?? data.name)
    .join(', ');
  if (openDialog) return renderTooltipWithIcon(val, col, params, openDialog);
  return <span className="overflow-hidden overflow-ellipsis">{val}</span>;
};

/** @private Renders an ArrayChips cell: tooltip+icon when a dialog is available, plain text otherwise. */
const renderArrayChipsCell = (
  fieldValue: unknown,
  col: ITableHeaders,
  params: GridRenderCellParams,
  openDialog?: IOpenDialogFn
): JSX.Element => {
  const val = fieldValue ? (fieldValue as string[]).join(', ') : '';
  if (openDialog) return renderTooltipWithIcon(val, col, params, openDialog);
  return <span className="overflow-hidden overflow-ellipsis">{val}</span>;
};

/** @private Renders the SyncStatus info icon; returns null when no dialog is provided. */
const renderSyncStatusCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog?: IOpenDialogFn
): JSX.Element | null => {
  if (!openDialog) return null;
  const status = renderSyncStatus(params.row.syncStatus as string);
  return InfoIconWithColor(params, openDialog, col, status);
};

/** @private Renders the Details (info) icon cell; returns null when no dialog is provided. */
const renderDetailsCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog?: IOpenDialogFn
): JSX.Element | null => {
  if (!openDialog) return null;
  return (
    <IconButton
      data-testid="btn-info-dialog"
      onClick={() => openDialog(params.row, col.type as string, EActionType.View, col)}
    >
      <InfoIcon color="primary" />
    </IconButton>
  );
};

/** @private Renders the Refresh icon cell that re-fetches the row's data. */
const renderRefreshCell = (
  params: GridRenderCellParams,
  type: string,
  props?: IMaintenanceTableProps
): JSX.Element => {
  const searchCode =
    (params.row[foreignKeysForTable[type]] as string) || (params.row.code as string);
  const handleClick = (): void => {
    if (props?.getDataList) {
      void AppStore.dispatch(
        props.getDataList({ searchCode, caller: ECellType.Refresh } as IPaginationRequest)
      );
    }
  };
  return (
    <IconButton onClick={handleClick}>
      <RefreshIcon color="primary" />
    </IconButton>
  );
};

/** @private Renders the Error cell: article icon when an error string is present, null otherwise. */
const renderErrorCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  openDialog?: IOpenDialogFn
): JSX.Element | null => {
  if (!params.row.error || !(params.row.error as string).length || !openDialog) return null;
  return getArticleIcon(params, col, openDialog);
};

/**
 * Unified cell renderer for DataTable and ActionableDataTable.
 *
 * Renders the appropriate cell content based on the column's `ECellType`. Pass only the
 * context fields required by the cell types in use; unused options are safely ignored.
 *
 * @param params - MUI DataGrid render-cell params
 * @param col - Column header definition
 * @param options - Optional context: `openDialog` for dialog-based cells, `onCellClick`
 *   for Link cells in ActionableDataTable, `type`/`moduleType`/`props` for Chips/Refresh/Download
 */
export const renderCell = (
  params: GridRenderCellParams,
  col: ITableHeaders,
  options: IRenderCellOptions = {}
): JSX.Element | string | null => {
  const { openDialog, uniqueKey, type = '', moduleType = '', props, onCellClick } = options;
  const fieldValue = params.row[col.field];

  switch (col.type) {
    case ECellType.Link:
      return renderLinkCell(params, col, type, onCellClick, openDialog);
    case ECellType.Images:
      return openDialog ? getImages(params, col, openDialog) : null;
    case ECellType.LongSalesText:
      return (
        <Chip
          size="small"
          key={params.row[uniqueKey ?? '']}
          label={t('labels.view')}
          onClick={() => openDialog?.(params.row, ECommonDialogType.LongSalesText)}
          color="primary"
          variant="outlined"
        />
      );
    case ECellType.Boolean:
      return fieldValue ? t('labels.yes') : t('labels.no');
    case ECellType.Price: {
      const locale = (countryCodes as Record<string, { locale: string }>)[
        params.row['countryCode']
      ]['locale'];
      const price = (fieldValue as number).toLocaleString(`${locale}-u-nu-latn`, {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      });
      return <span className="overflow-hidden overflow-ellipsis">{price}</span>;
    }
    case ECellType.ObjectChips:
      return renderObjectChipsCell(fieldValue, col, params, openDialog);
    case ECellType.ArrayChips:
      return renderArrayChipsCell(fieldValue, col, params, openDialog);
    case ECellType.Object:
      return (
        <span className="overflow-hidden overflow-ellipsis">
          {getDefaultValue(fieldValue as IPropertyDefault)}
        </span>
      );
    case ECellType.Date: {
      const val = formatDateString(fieldValue as string);
      return <span className="overflow-hidden overflow-ellipsis">{val}</span>;
    }
    case ECellType.Update:
      return renderUpdateCell(fieldValue as string);
    case ECellType.Details:
      return renderDetailsCell(params, col, openDialog);
    case ECellType.Log:
      return openDialog ? getArticleIcon(params, col, openDialog) : null;
    case ECellType.SyncStatus:
      return renderSyncStatusCell(params, col, openDialog);
    case ECellType.Error:
      return renderErrorCell(params, col, openDialog);
    case ECellType.Chips:
      return renderChipsCell(params, type, openDialog, props);
    case ECellType.Status:
      return <div className="text-center">{showStatusOfNotification(params)}</div>;
    case ECellType.Refresh:
      return renderRefreshCell(params, type, props);
    case ECellType.MaxDate: {
      const datesArr = (params.row[col.key as string] as { modifiedOn: string }[]).map(
        (data) => data.modifiedOn
      );
      return <span className="overflow-hidden overflow-ellipsis">{getMaxDate(datesArr)}</span>;
    }
    case ECellType.Tooltip:
      return (
        <Tooltip title={params.row[col.field]} placement="bottom" arrow>
          <span className="overflow-hidden overflow-ellipsis">{params.row[col.field]}</span>
        </Tooltip>
      );
    case ECellType.LinearProgress:
      return <LinearProgressBar activities={params.row[col.field]} getStatusIcon={getStatusIcon} />;
    case ECellType.Download:
      return getDownloadIcon(
        params,
        moduleType,
        props?.download as
          | ((
              action: { productCode?: string; versionId?: string; id?: string },
              dispatch: AppDispatch
            ) => void)
          | undefined
      );
    default:
      return renderDefaultCell(params, col);
  }
};

// #endregion

// #region computeDisableDelete

/** Evaluates each element of a multi-field deleteField array against a row. */
const inverseCheckForArray = (
  deleteField: string[],
  row: IDataTableRow,
  inverseCheckForDelete: boolean[]
): boolean => {
  let result = false;
  for (let i = 0; i < deleteField.length; i++) {
    result =
      result === (inverseCheckForDelete[i] ? !row[deleteField[i]] : Boolean(row[deleteField[i]]));
  }
  return result;
};

/**
 * Pure-function equivalent of DataTable's `disableDeleteBtn`.
 *
 * Returns `true` (disabled) when no selected row meets the deletion criterion.
 * Uses `row.id ?? row.code` for ID matching so tables keyed by code (e.g. LOV) work correctly.
 *
 * @param currentPageData - Rows visible on the current page
 * @param selectionModel - Current DataGrid row selection
 * @param deleteField - Field name(s) that control deletion eligibility
 * @param inverseCheckForDelete - When true the field check is negated (e.g. !isActive)
 */
export const computeDisableDelete = (
  currentPageData: IDataTableRow[],
  selectionModel: GridRowSelectionModel,
  deleteField: string | string[],
  inverseCheckForDelete: boolean | boolean[]
): boolean => {
  // 'include' = ids are selected; 'exclude' = ids are DE-selected (Select All uses exclude mode)
  const isExcludeMode = selectionModel.type === 'exclude';
  const isRowSelected = (rowId: string | number): boolean =>
    isExcludeMode ? !selectionModel.ids.has(rowId) : selectionModel.ids.has(rowId);

  const selectedRows = currentPageData.filter((row) => isRowSelected(row.id ?? row.code ?? ''));
  if (selectedRows.length === 0) return true;

  const ineligibleCount = selectedRows.filter((row) => {
    if (Array.isArray(deleteField)) {
      return inverseCheckForArray(deleteField, row, inverseCheckForDelete as boolean[]);
    }
    return (inverseCheckForDelete as boolean) ? !row[deleteField] : Boolean(row[deleteField]);
  }).length;

  return !(selectedRows.length > 0 && ineligibleCount !== selectedRows.length);
};

// #endregion

// #region renderButtonSection

/** Static look and feel of a toolbar button; the behaviour comes from the parent's handler. */
interface IToolbarButtonPreset {
  /** i18n key for the button label. */
  labelKey: string;
  icon: JSX.Element;
  className: string;
  color?: 'primary' | 'error';
  id?: string;
  testId?: string;
}

/**
 * Presentation presets per button type.
 *
 * Adding a new toolbar button means adding an enum value and one entry here — no new props
 * on the table, the toolbar, or this helper.
 */
const toolbarButtonPresets: Partial<Record<EToolbarButton, IToolbarButtonPreset>> = {
  [EToolbarButton.Delete]: {
    labelKey: 'button.delete',
    icon: <DeleteIcon />,
    className: 'delete-button common-btn',
    color: 'error',
    testId: 'actionable-table-delete-button',
  },
  [EToolbarButton.Create]: {
    labelKey: 'button.new',
    icon: <AddIcon />,
    className: 'text-capitalize common-btn',
    testId: 'actionable-table-create-button',
  },
  [EToolbarButton.Compare]: {
    labelKey: 'button.compare',
    icon: <CompareIcon />,
    className: 'common-btn text-capitalize',
    id: 'publish-button',
    testId: 'actionable-table-compare-button',
  },
};

/**
 * Renders the action buttons requested by the parent toolbar.
 *
 * The `headerActions` map carries both which buttons to render and what each one does, so a
 * new button never adds a prop here.
 *
 * @param props.headerActions - Header actions to render, each mapped to its click handler
 * @param props.disableDelete - When true the Delete button is disabled
 */
export const renderButtonSection = (props: IButtonSectionHelperProps): JSX.Element | null => {
  const { headerActions, disableDelete } = props;
  const entries = Object.entries(headerActions ?? {}) as [
    EToolbarButton,
    (() => void) | undefined,
  ][];
  if (!entries.length) return null;

  const renderButton = (name: EToolbarButton, onClick?: () => void): JSX.Element | null => {
    const preset = toolbarButtonPresets[name];
    if (!preset) return null;

    return (
      <Button
        key={name}
        id={preset.id}
        data-testid={preset.testId}
        className={preset.className}
        color={preset.color ?? 'primary'}
        variant="contained"
        disabled={name === EToolbarButton.Delete && disableDelete === true}
        startIcon={preset.icon}
        onClick={onClick}
      >
        {t(preset.labelKey)}
      </Button>
    );
  };

  return (
    <>
      {entries.map(([name, onClick], i) => (
        <span key={name}>
          {i > 0 && <>&emsp;</>}
          {renderButton(name, onClick)}
        </span>
      ))}
    </>
  );
};

// #endregion

// #region renderEditCell

/**
 * Renders the appropriate inline-edit control for a cell in ActionableDataTable.
 *
 * Called only when a row is in edit mode. Switches on the column type to return
 * the correct input: a Yes/No Select for Boolean columns, or a TextField for all others.
 *
 * @param field - The column field name
 * @param type - The ECellType of the column being edited
 * @param rowId - Id of the row being edited, passed through to keyboard handlers
 * @param row - Full row data, passed through to keyboard handlers
 * @param editValues - Current in-progress edit values keyed by field name
 * @param isDisabledByInactive - When true, text inputs are disabled (the row is inactive)
 * @param onFieldChange - Called when the input value changes
 * @param onKeyDown - Called on keydown; Enter saves, Escape cancels
 */
export const renderEditCell = (
  field: string,
  type: ECellType | undefined,
  rowId: string,
  row: IDataTableRow,
  editValues: Record<string, string>,
  isDisabledByInactive: boolean,
  callbacks: IRenderEditCellCallbacks
): JSX.Element => {
  if (type === ECellType.Boolean) {
    return (
      <Select
        value={editValues[field] ?? 'false'}
        onChange={(e) => callbacks.onFieldChange(field, e.target.value)}
        size="small"
        fullWidth
        sx={{
          height: 28,
          '& .MuiSelect-select': { py: 0, display: 'flex', alignItems: 'center' },
        }}
      >
        <MenuItem value="true">{t('labels.yes')}</MenuItem>
        <MenuItem value="false">{t('labels.no')}</MenuItem>
      </Select>
    );
  }
  return (
    <TextField
      value={editValues[field] ?? ''}
      onChange={(e) => callbacks.onFieldChange(field, e.target.value)}
      size="small"
      variant="outlined"
      autoFocus
      fullWidth
      disabled={isDisabledByInactive}
      onKeyDown={(e) => callbacks.onKeyDown(e, rowId, row)}
      sx={{
        '& .MuiOutlinedInput-root': { height: 28 },
        '& .MuiOutlinedInput-input': { py: 0, px: 1 },
      }}
    />
  );
};

// #endregion
